import { getCoreDialog } from 'cw-ui-core';
import React from 'react';
import LoadingDialog, { LOADING_DIALOG } from 'src/components/LoadingDialog';
import {
    CLOSE_WARNING_DIALOG,
    DELETE_CUSTOM_ROLE_DIALOG,
    DUPLICATE_APP_ROLES_DIALOG,
} from 'src/features/application-roles/constants';
import { DEFAULT_LOADING_DIALOG } from 'src/components/DefaultLoadingDialog/DefaultLoadingDialog';
import { DELETE_APPLICATION_DIALOG } from 'src/features/applications/components/DeleteApplicationDialog';
import { VIEW_ENDPOINTS_DIALOG } from 'src/features/applications/components/ViewEndpointsDialog';
import { CONFIRM_APPLICATION_CHANGES_DIALOG } from 'src/features/applications/components/ConfirmApplicationChangesDialog';
import { CANCEL_APPLICATION_DIALOG } from 'src/features/applications/components/CancelApplicationDialog';
import { APPROVE_IMPORTED_USER_DIALOG } from 'src/features/users/components/ImportedUsers/ApproveImportedUserDialog/ApproveImportedUserDialog';
import { APPROVE_MATCHED_CONTACTS_DIALOG } from 'src/features/users/components/ImportedUsers/ApproveMatchedContactsDialog/ApproveMatchedContactsDialog';
import { DELETE_IMPORTED_USER_DIALOG } from 'src/features/users/components/ImportedUsers/DeleteImportedUserDialog/DeleteImportedUserDialog';
import { EDIT_IMPORTED_USER_DIALOG } from 'src/features/users/components/ImportedUsers/EditImportedUserDialog/EditImportedUserDialog';
import {
    AUTH_PROVIDER_DELETE_DIALOG,
    AUTH_PROVIDER_DISABLE_DIALOG,
    AUTH_PROVIDER_EDIT_ASSIGNED_USERS_DIALOG,
    AUTH_PROVIDER_ENABLE_DIALOG,
} from 'src/features/authentication-v2/constants';
import {
    NEW_AUTHENTICATION_METHOD_USER_DIALOG,
    NEW_DELETE_USERS_DIALOG,
    NEW_DISABLE_USER_DIALOG,
    NEW_ENABLE_USER_DIALOG,
    NEW_RESEND_INVITE_USER_DIALOG,
    NEW_RESET_PASSWORD_USER_DIALOG,
} from 'src/features/users-v2/constants';
import { CANCEL_PRODUCT_DIALOG } from 'src/features/products/components/CancelProductDialog/CancelProductDialog';
import { DELETE_PRODUCT_INSTANCE_DIALOG } from 'src/features/products/components/DeleteProductInstanceDialog/DeleteProductInstanceDialog';
import { EDIT_PRODUCT_INSTANCE_DIALOG } from 'src/features/products/components/EditProductInstanceDialog/EditProductInstanceDialog';
import { GENERATE_TOKEN_DIALOG } from 'src/features/products/components/GenerateTokenDialog/GenerateTokenDialog';
import { DELETE_ROLE_DIALOG } from 'src/features/identity-roles/components/DeleteRoleDialog/DeleteRoleDialog';
import { SUPPORT_ENABLED_DIALOG } from 'src/pages/Support/components/SupportEnabledDialog/SupportEnabledDialog';
import { AUTHENTICATION_METHOD_USER_DIALOG } from 'src/features/users/components/AuthenticationMethodDialog/AuthenticationMethodDialog';
import { DELETE_USER_DIALOG } from 'src/features/users/components/DeleteUserDialog/DeleteUserDialog';
import { DISABLE_USER_DIALOG } from 'src/features/users/components/DisableUserDialog/DisableUserDialog';
import { DOWNLOAD_USERS_DIALOG } from 'src/features/users/components/DownloadUsersDialog/DownloadUsersDialog';
import { ENABLE_USER_DIALOG } from 'src/features/users/components/EnableUserDialog/EnableUserDialog';
import { FORGET_MFA_USER_DIALOG } from 'src/features/users/components/ForgetMFAUserDialog/ForgetMFAUserDialog';
import { MANAGE_APPLICATION_USER_DIALOG } from 'src/features/users/components/ManageApplicationDialog/ManageApplicationDialog';
import { NEW_DELETE_USER_DIALOG } from 'src/features/users/components/NewDeleteUserDialog/NewDeleteUserDialog';
import { NEW_MANAGE_ROLE_USER_DIALOG } from 'src/features/users/components/NewManageRolesDialog/NewManageRolesDialog';
import { RESEND_INVITE_USER_DIALOG } from 'src/features/users/components/ResendInviteUserDialog/ResendInviteUserDialog';
import { RESET_PASSWORD_USER_DIALOG } from 'src/features/users/components/ResetPasswordUserDialog/ResetPasswordUserDialog';
import { UMS_LOADING_DIALOG } from 'src/features/users/components/UMSLoadingDialog/UMSLoadingDialog';
import AsioNeonResponsiveThemeProvider from 'src/theme/AsioNeonResponsiveThemeProvider';
import { UPDATE_MFA_USER_DIALOG } from '../features/users/components/UpdateMfaDialog/UpdateMfaDialog';

// Lazy load the dialog components
const LazyDownloadUsersDialogContainer = React.lazy(
    () => import('src/features/users/components/DownloadUsersDialog/DownloadUsersDialogContainer')
);
const LazyDeleteUserDialogContainer = React.lazy(
    () => import('src/features/users/components/DeleteUserDialog/DeleteUserDialogContainer')
);
const LazyNewDeleteUserDialogContainer = React.lazy(
    () => import('src/features/users/components/NewDeleteUserDialog/NewDeleteUserDialogContainer')
);
const LazyEnableUserDialogContainer = React.lazy(
    () => import('src/features/users/components/EnableUserDialog/EnableUserDialogContainer')
);
const LazyResetPasswordUserDialogContainer = React.lazy(
    () => import('src/features/users/components/ResetPasswordUserDialog/ResetPasswordUserDialogContainer')
);
const LazyDisableUserDialogContainer = React.lazy(
    () => import('src/features/users/components/DisableUserDialog/DisableUserDialogContainer')
);
const LazyResendInviteUserDialogContainer = React.lazy(
    () => import('src/features/users/components/ResendInviteUserDialog/ResendInviteUserDialogContainer')
);
const LazyAuthenticationMethodDialogContainer = React.lazy(
    () => import('../features/users/components/AuthenticationMethodDialog/AuthenticationMethodDialogContainer')
);
const LazyNewManageRolesDialogContainer = React.lazy(
    () => import('../features/users/components/NewManageRolesDialog/NewManageRolesDialogContainer')
);
const LazyManageApplicationDialogContainer = React.lazy(
    () => import('../features/users/components/ManageApplicationDialog/ManageApplicationDialogContainer')
);
const LazyForgetMFAUserDialogContainer = React.lazy(
    () => import('../features/users/components/ForgetMFAUserDialog/ForgetMFAUserDialogContainer')
);
const LazyDeleteRoleDialogContainer = React.lazy(
    () => import('../features/identity-roles/components/DeleteRoleDialog/DeleteRoleDialogContainer')
);
const LazyGenerateTokenDialogContainer = React.lazy(
    () => import('../features/products/components/GenerateTokenDialog/GenerateTokenDialogContainer')
);
const LazyEditImportedUserDialogContainer = React.lazy(
    () => import('../features/users/components/ImportedUsers/EditImportedUserDialog/EditImportedUserDialogContainer')
);
const LazyApproveImportedUserDialogContainer = React.lazy(
    () =>
        import('src/features/users/components/ImportedUsers/ApproveImportedUserDialog/ApproveImportedUserDialogContainer')
);
const LazyDeleteImportedUserDialogContainer = React.lazy(
    () =>
        import('../features/users/components/ImportedUsers/DeleteImportedUserDialog/DeleteImportedUserDialogContainer')
);
const LazyEditProductInstanceDialogContainer = React.lazy(
    () => import('../features/products/components/EditProductInstanceDialog/EditProductInstanceDialogContainer')
);
const LazyDeleteProductInstanceDialogContainer = React.lazy(
    () => import('../features/products/components/DeleteProductInstanceDialog/DeleteProductInstanceDialogContainer')
);
const LazyDeleteApplicationContainer = React.lazy(
    () => import('../features/applications/components/DeleteApplicationContainer')
);
const LazySupportEnabledDialogContainer = React.lazy(
    () => import('../pages/Support/components/SupportEnabledDialog/SupportEnabledDialogContainer')
);
const LazyLoadingDialog = React.lazy(() => import('./LoadingDialog'));
const LazyDefaultLoadingDialog = React.lazy(() => import('./DefaultLoadingDialog/DefaultLoadingDialog'));
const LazyUMSLoadingDialog = React.lazy(
    () => import('src/features/users/components/UMSLoadingDialog/UMSLoadingDialog')
);
const LazyUpdateMfaDialogContainer = React.lazy(
    () => import('../features/users/components/UpdateMfaDialog/UpdateMfaDialogContainer')
);
const LazyViewEndpointsDialogContainer = React.lazy(
    () => import('../features/applications/components/ViewEndpointsDialogContainer')
);
const LazyCancelApplicationDialogContainer = React.lazy(
    () => import('../features/applications/components/CancelApplicationDialogContainer')
);
const LazyConfirmApplicationChangesDialogContainer = React.lazy(
    () => import('../features/applications/components/ConfirmApplicationChangesDialogContainer')
);
const LazyApproveMatchedContactsDialogContainer = React.lazy(
    () =>
        import('../features/users/components/ImportedUsers/ApproveMatchedContactsDialog/ApproveMatchedContactsDialogContainer')
);
const LazyCancelProductDialogContainer = React.lazy(
    () => import('../features/products/components/CancelProductDialog/CancelProductDialogContainer')
);
const LazyCloseWarningDialogContainer = React.lazy(
    () => import('../features/application-roles/components/CloseWarningDialog/CloseWarningDialogContainer')
);
const LazyAuthProviderEnableDialogContainer = React.lazy(
    () => import('../features/authentication-v2/components/AuthProviderEnableDialog/AuthProviderEnableDialogContainer')
);
const LazyAuthProviderDisableDialogContainer = React.lazy(
    () =>
        import('../features/authentication-v2/components/AuthProviderDisableDialog/AuthProviderDisableDialogContainer')
);
const LazyAuthProviderDeleteDialogContainer = React.lazy(
    () => import('../features/authentication-v2/components/AuthProviderDeleteDialog/AuthProviderDeleteDialogContainer')
);
const LazyDeleteCustomRoleDialogContainer = React.lazy(
    () => import('../features/application-roles/components/DeleteCustomRoleDialog/DeleteCustomRoleDialogContainer')
);
const LazyNewResendInviteUserDialogContainer = React.lazy(
    () => import('src/features/users-v2/components/NewResendInviteUserDialog/NewResendInviteUserDialogContainer')
);
const LazyAuthProviderEditAssignedUsersDialogContainer = React.lazy(
    () =>
        import('../features/authentication-v2/components/AuthProviderEditAssignedUsersDialog/AuthProviderEditAssignedUsersDialogContainer')
);
const LazyNewResetPasswordUserDialogContainer = React.lazy(
    () => import('src/features/users-v2/components/NewResetPasswordUserDialog/NewResetPasswordUserDialogContainer')
);
const LazyNewAuthenticationMethodDialogContainer = React.lazy(
    () =>
        import('src/features/users-v2/components/NewAuthenticationMethodDialog/NewAuthenticationMethodDialogContainer')
);
const LazyNewDisableUserDialogContainer = React.lazy(
    () => import('src/features/users-v2/components/NewDisableUserDialog/NewDisableUserDialogContainer')
);
const LazyNewEnableUserDialogContainer = React.lazy(
    () => import('src/features/users-v2/components/NewEnableUserDialog/NewEnableUserDialogContainer')
);
const LazyNewUserNewDeleteUserDialogContainer = React.lazy(
    () => import('src/features/users-v2/components/NewDeleteUserDialog/NewDeleteUserDialogContainer')
);
const LazyDuplicateAppRolesDialogContainer = React.lazy(
    () => import('../features/application-roles/components/DuplicateAppRolesDialog/DuplicateAppRolesDialogContainer')
);

const getDialog = (dialogID, props = null) => {
    switch (dialogID) {
        case DOWNLOAD_USERS_DIALOG:
            return <LazyDownloadUsersDialogContainer key={dialogID} {...props} />;
        case DELETE_USER_DIALOG:
            return <LazyDeleteUserDialogContainer key={dialogID} {...props} />;

        case NEW_DELETE_USER_DIALOG:
            return <LazyNewDeleteUserDialogContainer key={dialogID} {...props} />;

        case ENABLE_USER_DIALOG:
            return <LazyEnableUserDialogContainer key={dialogID} {...props} />;

        case RESET_PASSWORD_USER_DIALOG:
            return <LazyResetPasswordUserDialogContainer key={dialogID} {...props} />;

        case DISABLE_USER_DIALOG:
            return <LazyDisableUserDialogContainer key={dialogID} {...props} />;

        case RESEND_INVITE_USER_DIALOG:
            return <LazyResendInviteUserDialogContainer key={dialogID} {...props} />;

        case AUTHENTICATION_METHOD_USER_DIALOG:
            return <LazyAuthenticationMethodDialogContainer key={dialogID} {...props} />;

        case NEW_MANAGE_ROLE_USER_DIALOG:
            return <LazyNewManageRolesDialogContainer key={dialogID} {...props} />;

        case MANAGE_APPLICATION_USER_DIALOG:
            return <LazyManageApplicationDialogContainer key={dialogID} {...props} />;

        case FORGET_MFA_USER_DIALOG:
            return <LazyForgetMFAUserDialogContainer key={dialogID} {...props} />;

        case DELETE_ROLE_DIALOG:
            return <LazyDeleteRoleDialogContainer key={dialogID} {...props} />;

        case GENERATE_TOKEN_DIALOG:
            return <LazyGenerateTokenDialogContainer key={dialogID} {...props} />;

        case EDIT_IMPORTED_USER_DIALOG:
            return <LazyEditImportedUserDialogContainer key={dialogID} {...props} />;

        case APPROVE_IMPORTED_USER_DIALOG:
            return <LazyApproveImportedUserDialogContainer key={dialogID} {...props} />;

        case DELETE_IMPORTED_USER_DIALOG:
            return <LazyDeleteImportedUserDialogContainer key={dialogID} {...props} />;

        case EDIT_PRODUCT_INSTANCE_DIALOG:
            return <LazyEditProductInstanceDialogContainer key={dialogID} {...props} />;

        case DELETE_PRODUCT_INSTANCE_DIALOG:
            return <LazyDeleteProductInstanceDialogContainer key={dialogID} {...props} />;

        case DELETE_APPLICATION_DIALOG:
            return <LazyDeleteApplicationContainer key={dialogID} {...props} />;

        case SUPPORT_ENABLED_DIALOG:
            return <LazySupportEnabledDialogContainer key={dialogID} {...props} />;

        case LOADING_DIALOG:
            return <LazyLoadingDialog key={dialogID} {...props} />;

        case DEFAULT_LOADING_DIALOG:
            return <LazyDefaultLoadingDialog key={dialogID} {...props} />;

        case UMS_LOADING_DIALOG:
            return <LazyUMSLoadingDialog key={dialogID} {...props} />;

        case UPDATE_MFA_USER_DIALOG:
            return <LazyUpdateMfaDialogContainer key={dialogID} {...props} />;

        case VIEW_ENDPOINTS_DIALOG:
            return <LazyViewEndpointsDialogContainer key={dialogID} {...props} />;

        case CANCEL_APPLICATION_DIALOG:
            return <LazyCancelApplicationDialogContainer key={dialogID} {...props} />;

        case CONFIRM_APPLICATION_CHANGES_DIALOG:
            return <LazyConfirmApplicationChangesDialogContainer key={dialogID} {...props} />;

        case APPROVE_MATCHED_CONTACTS_DIALOG:
            return <LazyApproveMatchedContactsDialogContainer key={dialogID} {...props} />;

        case CANCEL_PRODUCT_DIALOG:
            return <LazyCancelProductDialogContainer key={dialogID} {...props} />;

        case CLOSE_WARNING_DIALOG:
            return <LazyCloseWarningDialogContainer key={dialogID} {...props} />;

        case AUTH_PROVIDER_ENABLE_DIALOG:
            return <LazyAuthProviderEnableDialogContainer key={dialogID} {...props} />;

        case AUTH_PROVIDER_DISABLE_DIALOG:
            return <LazyAuthProviderDisableDialogContainer key={dialogID} {...props} />;

        case AUTH_PROVIDER_DELETE_DIALOG:
            return <LazyAuthProviderDeleteDialogContainer key={dialogID} {...props} />;

        case DELETE_CUSTOM_ROLE_DIALOG:
            return <LazyDeleteCustomRoleDialogContainer key={dialogID} {...props} />;

        case NEW_RESEND_INVITE_USER_DIALOG:
            return <LazyNewResendInviteUserDialogContainer key={dialogID} {...props} />;

        case AUTH_PROVIDER_EDIT_ASSIGNED_USERS_DIALOG:
            return <LazyAuthProviderEditAssignedUsersDialogContainer key={dialogID} {...props} />;

        case NEW_RESET_PASSWORD_USER_DIALOG:
            return <LazyNewResetPasswordUserDialogContainer key={dialogID} {...props} />;

        case NEW_AUTHENTICATION_METHOD_USER_DIALOG:
            return <LazyNewAuthenticationMethodDialogContainer key={dialogID} {...props} />;

        case NEW_DISABLE_USER_DIALOG:
            return <LazyNewDisableUserDialogContainer key={dialogID} {...props} />;

        case NEW_ENABLE_USER_DIALOG:
            return <LazyNewEnableUserDialogContainer key={dialogID} {...props} />;

        case NEW_DELETE_USERS_DIALOG:
            return <LazyNewUserNewDeleteUserDialogContainer key={dialogID} {...props} />;

        case DUPLICATE_APP_ROLES_DIALOG:
            return <LazyDuplicateAppRolesDialogContainer key={dialogID} {...props} />;

        default:
            return getCoreDialog(dialogID, props);
    }
};

const getDialogFactory = (dialogID, props) => {
    if (!dialogID) {
        return null;
    }
    return (
        <AsioNeonResponsiveThemeProvider>
            <React.Suspense fallback={<LoadingDialog />}>{getDialog(dialogID, props)}</React.Suspense>
        </AsioNeonResponsiveThemeProvider>
    );
};

export default getDialogFactory;
